import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { ThemeContext } from '@susu/custom-made/providers';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type {
  Monogram,
  MonogramItem,
  Option,
  OptionConfigurationType,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getSectionByConfigurationType } from '@susu/custom-made/utils/navigation';
import type {
  MissingMonogramValuesPerConfig,
  MissingOptions,
  MissingOptionsPerConfiguration,
  MissingOptionValues,
  MissingOptionValuesPerConfiguration,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import { getOptionById } from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import {
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
  type ConfigurationTypes,
} from '@susu/custom-made/utils/productDefinition';
import {
  capitalizeFirstLetter,
  getImagePaths,
  getImageUrl,
  isAdditonalProduct,
  isObjectEmpty,
} from '@susu/custom-made/utils/utils';
import type { SyntheticEvent } from 'react';
import React, { useContext, useMemo, useState } from 'react';

import { missingOptionDetailsText } from './missingOptionDetailsConstants';
import styles from './missingOptionsDetails.module.scss';

type MissingOptionsDetailsProps = {
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  missingOptionValuesPerConfig: MissingOptionValuesPerConfiguration;
  missingMonogramValuesPerConfig: MissingMonogramValuesPerConfig;
  missingOptionsPerConfig: MissingOptionsPerConfiguration;
  isSummaryValidationFlow: boolean;
};

type FormattedOptionValue = {
  optionName: string;
  optionId: number;
  optionValueName: string;
  optionThumbnail: string;
  optionFallbackThumbnail: string;
};

type FormattedOption = {
  optionName: string;
  optionId: number;
  optionThumbnail?: string;
  optionFallbackThumbnail?: string;
};

type FormattedMissingMonogramValue = {
  monogramId: number;
  monogramPosition: string | null;
  missingMonogramText: string;
  monogramThumbnail: string;
  isPositionMissing: boolean;
};

const MissingOptionsDetails = ({
  options,
  optionValues,
  missingOptionValuesPerConfig,
  missingMonogramValuesPerConfig,
  missingOptionsPerConfig,
  isSummaryValidationFlow,
}: MissingOptionsDetailsProps) => {
  const { state } = useContext(ThemeContext);
  const { missingValueClickHandler, removeSelectedMonogramFromMissingValues } = useMissingValues();
  const [fallbackImageUrlList, setFallbackImageUrlList] = useState<string[]>([]);
  const { title, description } = useMemo(() => {
    let title: string = isSummaryValidationFlow
      ? missingOptionDetailsText.summaryValidationFlow.title
      : missingOptionDetailsText.preloadFlow.title;
    let description: string = isSummaryValidationFlow
      ? missingOptionDetailsText.summaryValidationFlow.description
      : missingOptionDetailsText.preloadFlow.description;

    return { title, description };
  }, [isSummaryValidationFlow]);

  const getFormattedMissingOptionValuesData = (
    optionValues: MissingOptionValues,
    options: Option[],
  ): FormattedOptionValue[] => {
    return Object.values(optionValues).reduce<FormattedOptionValue[]>((acc, optionValue) => {
      const matchingOption = getOptionById(optionValue.optionId, options);

      if (matchingOption) {
        const { imageThumbnail, fallbackImageThumbnail } = getImagePaths(
          matchingOption.imageUrlTemplate,
          optionValue.code,
          state.selectedFabric?.layerImageName!,
        );

        acc.push({
          optionName: matchingOption.name,
          optionId: optionValue.optionId,
          optionValueName: optionValue.name,
          optionThumbnail: imageThumbnail,
          optionFallbackThumbnail: fallbackImageThumbnail,
        });
      }
      return acc;
    }, []);
  };

  const onImageFailed = (e: SyntheticEvent<HTMLImageElement, Event>, cardFallbackImageUrl?: string) => {
    if (cardFallbackImageUrl && fallbackImageUrlList.indexOf(cardFallbackImageUrl) < 0) {
      e.currentTarget.src = cardFallbackImageUrl;
      setFallbackImageUrlList([...fallbackImageUrlList, cardFallbackImageUrl]);
    }
  };

  const renderMissingOptionValues = () => {
    if (!missingOptionValuesPerConfig) {
      return;
    }

    return Object.entries(missingOptionValuesPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingOptionValues]) => {
        if (
          isAdditonalProduct(state.product!, configurationTypeId as ConfigurationTypes) &&
          !state.isAdditionalProductAdded[configurationTypeId]
        ) {
          return acc;
        }
        if (!isObjectEmpty(missingOptionValues)) {
          const associatedSection = getSectionByConfigurationType(configurationTypeId);
          const associatedSectionPresentationalName = capitalizeFirstLetter(
            getSectionByConfigurationType(configurationTypeId),
          );

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const configurationOptions = options[baseConfigurationTypeId].items;
          const formattedMissingValues = getFormattedMissingOptionValuesData(missingOptionValues, configurationOptions);

          const sectionElement = (
            <div
              className={styles.optionDetailContainer}
              key={`${configurationTypeId}-${associatedSectionPresentationalName}`}
            >
              <div className={styles.associatedSection}>{associatedSectionPresentationalName}</div>
              <div className={styles.missingValuesContainer}>
                {formattedMissingValues.map(missingValue => (
                  <div
                    key={`${missingValue.optionName}-${missingValue.optionValueName}`}
                    className={styles.missingValue}
                    data-option-id={missingValue.optionId}
                    data-associated-section={associatedSection}
                    onClick={e => {
                      missingValueClickHandler(e, configurationOptions);
                    }}
                  >
                    <img
                      src={missingValue.optionThumbnail}
                      className={styles.img}
                      alt="missing-option-img"
                      onError={e => {
                        onImageFailed(e, missingValue.optionFallbackThumbnail);
                      }}
                    />
                    <div className={styles.textDetails}>
                      <div className={styles.optionName}>{missingValue.optionName}</div>
                      <div>{missingValue.optionValueName}</div>
                    </div>
                    <div className={styles.iconHolder}>
                      <i className="susu-icons" translate="no">
                        arrow_right
                      </i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

          acc.push(sectionElement);
        }
        return acc;
      }, []);
  };

  const generateMissingMonogramDetails = (
    missingMonogramValue: MonogramItem,
    monogramOption: Option,
    monogramPositionId: number,
  ) => {
    const isPositionMissing: boolean =
      Object.keys(missingMonogramValue).length === 1 && Object.keys(missingMonogramValue).includes('positionName');
    const affectedMonogramPosition =
      missingMonogramValue.positionName ?? monogramOption.positions![+monogramPositionId]?.name;
    let missingMonogramValuesKeys: string[] = [];

    Object.keys(missingMonogramValue).forEach(monogramValueKey => {
      if (monogramValueKey === 'colorId') {
        const missingColor = missingMonogramValue['colorName'] ? `${missingMonogramValue['colorName']} Color` : 'Color';
        missingMonogramValuesKeys.push(missingColor);
      }

      if (monogramValueKey === 'fontStyleId') {
        const missingFont = missingMonogramValue['fontName'] ? `${missingMonogramValue['fontName']} Font` : 'Font';
        missingMonogramValuesKeys.push(missingFont);
      }
    });

    const missingMonogramText =
      missingMonogramValuesKeys.length > 0 ? missingMonogramValuesKeys.toString() : 'Position';

    return {
      missingMonogramText: missingMonogramText,
      affectedMonogramPosition: affectedMonogramPosition,
      isPositionMissing: isPositionMissing,
    };
  };

  const getFormattedMissingMonogramValuesData = (
    monogramValues: Monogram,
    options: Option[],
  ): FormattedMissingMonogramValue[] => {
    return Object.entries(monogramValues).reduce<FormattedMissingMonogramValue[]>(
      (acc, [monogramPositionId, monogramValue]) => {
        const monogramOption = options.find(option => option.type === 'MONOGRAM');
        if (!monogramOption) {
          return acc;
        }

        const monogramThumbnail = imageUrlBuilder({
          imageUrls: [
            getImageUrl(
              monogramOption.imageUrlTemplate,
              monogramOption.imageName,
              state.selectedFabric?.layerImageName!,
            ).imageUrl,
          ],
          imageDefinition: imageDefinition.thumbnail,
          noTransformation: true,
        }).src;

        const { missingMonogramText, affectedMonogramPosition, isPositionMissing } = generateMissingMonogramDetails(
          monogramValue,
          monogramOption,
          +monogramPositionId,
        );

        acc.push({
          monogramId: +monogramPositionId,
          monogramPosition: affectedMonogramPosition,
          missingMonogramText,
          monogramThumbnail,
          isPositionMissing: isPositionMissing,
        });

        return acc;
      },
      [],
    );
  };

  const renderMissingMonogramValues = () => {
    if (!missingMonogramValuesPerConfig) {
      return;
    }

    return Object.entries(missingMonogramValuesPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingMonogramValues]) => {
        const associatedSection = getSectionByConfigurationType(configurationTypeId);
        const associatedSectionPresentationalName = capitalizeFirstLetter(
          getSectionByConfigurationType(configurationTypeId),
        );
        // To handle extra configurations which have different configuration type id but using definitions from base product
        const baseConfigurationTypeId =
          getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
        const configurationOptions = options[baseConfigurationTypeId].items;
        const formattedMonogramValues = getFormattedMissingMonogramValuesData(
          missingMonogramValues,
          configurationOptions,
        );

        const sectionElement = (
          <div className={styles.optionDetailContainer} key={'missing-monograms'}>
            <div className={styles.associatedSection}>Monograms</div>
            <div className={styles.missingValuesContainer}>
              {formattedMonogramValues.map(missingValue => (
                <div
                  key={`${missingValue.monogramId}`}
                  className={styles.missingValue}
                  data-position-id={missingValue.monogramId}
                  data-associated-section={associatedSection}
                  onClick={e => {
                    missingValueClickHandler(e, configurationOptions);
                  }}
                >
                  <img src={missingValue.monogramThumbnail} className={styles.img} alt="missing-monogram-img" />
                  <div className={styles.textDetails}>
                    <div className={styles.optionName}>
                      {missingValue.monogramPosition
                        ? missingValue.monogramPosition
                        : associatedSectionPresentationalName}
                    </div>
                    <div>{missingValue.missingMonogramText}</div>
                    {missingValue.isPositionMissing && <div className={styles.errorText}>N/A Please remove</div>}
                  </div>
                  {missingValue.isPositionMissing && (
                    <div
                      className={styles.iconHolder}
                      onClick={e => {
                        e.stopPropagation();
                        removeSelectedMonogramFromMissingValues(configurationTypeId, missingValue.monogramId);
                      }}
                    >
                      <i className="susu-icons" translate="no">
                        trash
                      </i>
                    </div>
                  )}

                  <div className={styles.iconHolder}>
                    <i className="susu-icons" translate="no">
                      arrow_right
                    </i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

        acc.push(sectionElement);
        return acc;
      }, []);
  };

  const getFormattedOptionsWithMissingSelectionData = (
    missingOptions: MissingOptions,
    options: Option[],
  ): FormattedOption[] => {
    return Object.values(missingOptions).reduce<FormattedOption[]>((acc, missingOption) => {
      const matchingOption = getOptionById(missingOption.id, options);

      if (matchingOption) {
        const firstOptionValueOfMissingOption = optionValues[matchingOption.optionValues![0]];

        let formattedMissingOption: FormattedOption = {
          optionName: matchingOption.name,
          optionId: matchingOption.id,
        };

        if (firstOptionValueOfMissingOption) {
          const { imageThumbnail, fallbackImageThumbnail } = getImagePaths(
            matchingOption.imageUrlTemplate,
            firstOptionValueOfMissingOption.code,
            state.selectedFabric?.layerImageName!,
          );

          formattedMissingOption.optionThumbnail = imageThumbnail;
          formattedMissingOption.optionFallbackThumbnail = fallbackImageThumbnail;
        }

        acc.push(formattedMissingOption);
      }

      return acc;
    }, []);
  };

  const renderOptionsWithMissingSelection = () => {
    if (!missingOptionsPerConfig || isObjectEmpty(missingOptionsPerConfig)) {
      return;
    }

    return Object.entries(missingOptionsPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingOptions]) => {
        if (
          isAdditonalProduct(state.product!, configurationTypeId as ConfigurationTypes) &&
          !state.isAdditionalProductAdded[configurationTypeId]
        ) {
          return acc;
        }

        if (!isObjectEmpty(missingOptions)) {
          const associatedSection = getSectionByConfigurationType(configurationTypeId);
          const associatedSectionPresentationalName = capitalizeFirstLetter(
            getSectionByConfigurationType(configurationTypeId),
          );

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const configurationOptions = options[baseConfigurationTypeId].items;
          const formattedMissingValues = getFormattedOptionsWithMissingSelectionData(
            missingOptions,
            configurationOptions,
          );

          const sectionElement = (
            <div
              className={styles.optionDetailContainer}
              key={`${configurationTypeId}-${associatedSectionPresentationalName}`}
            >
              <div className={styles.associatedSection}>{associatedSectionPresentationalName}</div>
              <div className={styles.missingValuesContainer}>
                {formattedMissingValues.map(missingValue => (
                  <div
                    key={`${missingValue.optionName}`}
                    className={styles.missingValue}
                    data-option-id={missingValue.optionId}
                    data-associated-section={associatedSection}
                    onClick={e => {
                      missingValueClickHandler(e, configurationOptions);
                    }}
                  >
                    <img
                      src={missingValue.optionThumbnail}
                      className={styles.img}
                      alt="missing-option-img"
                      onError={e => {
                        onImageFailed(e, missingValue.optionFallbackThumbnail);
                      }}
                    />
                    <div className={styles.textDetails}>
                      <div className={styles.optionName}>{missingValue.optionName}</div>
                    </div>
                    <div className={styles.iconHolder}>
                      <i className="susu-icons" translate="no">
                        arrow_right
                      </i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

          acc.push(sectionElement);
        }
        return acc;
      }, []);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContent}>
        <div className={styles.iconHolder} />
        <div className={styles.title}>{title}</div>
        <div className={styles.description}>{description}</div>
      </div>
      {renderMissingOptionValues()}
      {isSummaryValidationFlow && renderOptionsWithMissingSelection()}
      {renderMissingMonogramValues()}
    </div>
  );
};

export default React.memo(MissingOptionsDetails);
